import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FilterChipProps {
  label: string;
  count?: number;
  unit?: string;
  premium?: number;
  selected?: boolean;
  onClick?: () => void;
  accent?: "green" | "yellow" | "red";
  tooltip?: string;
  "data-testid"?: string;
}

const SELECTED_CLASSES: Record<NonNullable<FilterChipProps["accent"]>, string> = {
  green: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  yellow: "bg-amber-500/10 text-amber-700 border-amber-200",
  red: "bg-red-500/10 text-red-700 border-red-200",
};

function formatPremium(premium: number): string {
  if (!Number.isFinite(premium) || premium <= 0) return "—";
  if (premium >= 1_000_000) return `$${(premium / 1_000_000).toFixed(1)}M`;
  if (premium >= 1_000) return `$${(premium / 1_000).toFixed(1)}k`;
  return `$${Math.round(premium).toLocaleString()}`;
}

function pluralize(word: string): string {
  if (word.endsWith("y")) return word.slice(0, -1) + "ies";
  return word + "s";
}

export function FilterChip({
  label,
  count,
  unit,
  premium,
  selected = false,
  onClick,
  accent = "green",
  tooltip,
  ...rest
}: FilterChipProps) {
  const hasCount = typeof count === "number";
  const hasPremium = typeof premium === "number";
  const unitWord = hasCount && unit ? (count === 1 ? unit : pluralize(unit)) : "";
  const interactive = !!onClick;
  const button = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-normal transition-colors",
        selected
          ? SELECTED_CLASSES[accent]
          : "bg-background text-muted-foreground border-border",
        interactive && "cursor-pointer",
        interactive && !selected && "hover:bg-muted/50",
      )}
      aria-pressed={selected}
      data-testid={rest["data-testid"]}
    >
      <span>
        <span className="font-semibold">{label}</span>
        {hasCount && (unitWord ? `: ${count.toLocaleString()} ${unitWord}` : `: ${count.toLocaleString()}`)}
        {hasPremium && ` (${formatPremium(premium)})`}
      </span>
    </button>
  );

  if (!tooltip) return button;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent className="max-w-xs text-center">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
